import { useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { PREFLIGHT_BRAND_NAME_MAX_LENGTH } from "@nexus/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
} from "@nexus/ui";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import { ApiError, getLocalUserFacingApiMessage } from "@/lib/api-client";
import {
  useCreateBrand,
  useDeleteBrand,
  useRenameBrand,
} from "../../hooks/usePreflightBrands";
import type { PreflightBrand } from "../../types/preflight.types";

const CONFLICT_STATUS = 409;
const ADD_INPUT_ID = "preflight-manage-brands-name";
/** 44px hit area on touch; compact again where a fine pointer can hover. */
const TOUCH_TARGET =
  "min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8";

export interface ManageBrandsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brands: PreflightBrand[];
}

/**
 * The tenant's controlled brand list (AD-3347). Adding, renaming and removing are
 * separate permissions, so each control only renders for a caller who holds its key.
 * Renaming or removing a brand never touches reports — each keeps the brand it was
 * scored for. The mutations opt out of the global error toast so a conflict is
 * surfaced here instead.
 */
export function ManageBrandsDialog({ open, onOpenChange, brands }: ManageBrandsDialogProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const canAdd = permissions.PREFLIGHT_CREATE_BRAND.isOn;
  const canRename = permissions.PREFLIGHT_UPDATE_BRAND.isOn;
  const canRemove = permissions.PREFLIGHT_DELETE_BRAND.isOn;
  const createMutation = useCreateBrand();
  const renameMutation = useRenameBrand();
  const deleteMutation = useDeleteBrand();

  const addInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const trimmedName = name.trim();

  function writeErrorMessage(error: Error, brandName: string): string | null {
    if (error instanceof ApiError && error.status === CONFLICT_STATUS) {
      return t("preflight.brands.error.alreadyAdded", "{{brand}} is already in your list.", {
        brand: brandName,
      });
    }
    return getLocalUserFacingApiMessage(error);
  }

  function handleAdd(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (trimmedName.length === 0) return;
    setFormError(null);

    createMutation.mutate(
      { name: trimmedName },
      {
        onSuccess: () => setName(""),
        onError: (error) => setFormError(writeErrorMessage(error, trimmedName)),
      },
    );
  }

  function handleRename(brandId: string, nextName: string) {
    setFormError(null);
    renameMutation.mutate(
      { id: brandId, name: nextName },
      {
        onSuccess: () => setEditingId(null),
        onError: (error) => setFormError(writeErrorMessage(error, nextName)),
      },
    );
  }

  function handleRemove(brandId: string) {
    setFormError(null);
    deleteMutation.mutate(brandId, {
      onError: (error) => setFormError(getLocalUserFacingApiMessage(error)),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        variant="form"
        className="sm:max-w-md"
        // Radix would focus the first row action, where a stray Enter deletes a brand.
        onOpenAutoFocus={(event) => {
          if (!addInputRef.current) return;
          event.preventDefault();
          addInputRef.current.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t("preflight.brands.manageTitle", "Manage brands")}</DialogTitle>
          <DialogDescription>
            {t(
              "preflight.brands.manageDescription",
              "The brands a report can be scored for, as they appear in your creatives. Renaming or removing one keeps the old name on reports that already use it.",
            )}
          </DialogDescription>
        </DialogHeader>

        {brands.length > 0 ? (
          <ul
            className="flex max-h-64 flex-col gap-1 overflow-y-auto"
            aria-label={t("preflight.brands.listLabel", "Brands")}
          >
            {brands.map((brand) => (
              <BrandRow
                key={brand.id}
                brand={brand}
                isEditing={editingId === brand.id}
                isBusy={renameMutation.isPending || deleteMutation.isPending}
                canRename={canRename}
                canRemove={canRemove}
                onStartEdit={() => {
                  setFormError(null);
                  setEditingId(brand.id);
                }}
                onCancelEdit={() => setEditingId(null)}
                onRename={(nextName) => handleRename(brand.id, nextName)}
                onRemove={() => handleRemove(brand.id)}
              />
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-xs">
            {canAdd
              ? t("preflight.brands.manageEmpty", "No brands yet — add the first one below.")
              : t("preflight.brands.manageEmptyReadOnly", "No brands yet.")}
          </p>
        )}

        {canAdd ? (
          <form className="flex items-start gap-2" onSubmit={handleAdd}>
            <Input
              ref={addInputRef}
              id={ADD_INPUT_ID}
              type="text"
              value={name}
              maxLength={PREFLIGHT_BRAND_NAME_MAX_LENGTH}
              placeholder={t("preflight.brands.addPlaceholder", "e.g. Vodafone")}
              aria-label={t("preflight.brands.addLabel", "Brand name")}
              aria-invalid={formError ? true : undefined}
              onChange={(event) => {
                setName(event.target.value);
                if (formError) setFormError(null);
              }}
              disabled={createMutation.isPending}
              className="min-w-0 flex-1"
            />
            <Button
              type="submit"
              variant="secondary"
              size="sm"
              iconStart="plus"
              className={TOUCH_TARGET}
              disabled={trimmedName.length === 0 || createMutation.isPending}
            >
              {t("preflight.brands.add", "Add")}
            </Button>
          </form>
        ) : null}

        {formError && (
          <p role="alert" className="text-error text-xs">
            {formError}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t("preflight.brands.manageDone", "Done")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface BrandRowProps {
  brand: PreflightBrand;
  isEditing: boolean;
  isBusy: boolean;
  canRename: boolean;
  canRemove: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onRename: (name: string) => void;
  onRemove: () => void;
}

function BrandRow({
  brand,
  isEditing,
  isBusy,
  canRename,
  canRemove,
  onStartEdit,
  onCancelEdit,
  onRename,
  onRemove,
}: BrandRowProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(brand.name);
  const trimmedDraft = draft.trim();
  const isUnchanged = trimmedDraft === brand.name;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (trimmedDraft.length === 0) return;
    if (isUnchanged) {
      onCancelEdit();
      return;
    }
    onRename(trimmedDraft);
  }

  // Escape cancels the edit; without stopping it here it would also close the dialog.
  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    onCancelEdit();
  }

  if (isEditing) {
    return (
      <li className="flex items-center gap-2 px-2 py-1">
        <form className="flex min-w-0 flex-1 items-center gap-2" onSubmit={handleSubmit}>
          <Input
            type="text"
            autoFocus
            value={draft}
            maxLength={PREFLIGHT_BRAND_NAME_MAX_LENGTH}
            aria-label={t("preflight.brands.renameLabel", "New name for {{name}}", {
              name: brand.name,
            })}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isBusy}
            className="min-w-0 flex-1"
          />
          <Button
            type="submit"
            variant="ghost"
            size="sm"
            className={TOUCH_TARGET}
            disabled={isBusy || trimmedDraft.length === 0}
            aria-label={t("preflight.brands.renameSave", "Save name")}
          >
            <Icon name="check" size="sm" color="inherit" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={TOUCH_TARGET}
            disabled={isBusy}
            aria-label={t("preflight.brands.renameCancel", "Cancel rename")}
            onClick={onCancelEdit}
          >
            <Icon name="x" size="sm" color="inherit" />
          </Button>
        </form>
      </li>
    );
  }

  return (
    <li className="hover:bg-muted flex items-center gap-1 rounded-md px-2 py-1.5">
      <span className="min-w-0 flex-1 truncate text-sm">{brand.name}</span>
      {canRename ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={TOUCH_TARGET}
          disabled={isBusy}
          aria-label={t("preflight.brands.renameAria", "Rename {{name}}", { name: brand.name })}
          onClick={() => {
            setDraft(brand.name);
            onStartEdit();
          }}
        >
          <Icon name="pencil" size="sm" color="inherit" />
        </Button>
      ) : null}
      {canRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={TOUCH_TARGET}
          disabled={isBusy}
          aria-label={t("preflight.brands.removeAria", "Remove {{name}}", { name: brand.name })}
          onClick={onRemove}
        >
          <Icon name="trash-2" size="sm" color="inherit" />
        </Button>
      ) : null}
    </li>
  );
}
